import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Brand from "@/components/Brand";
import FareCard from "@/components/FareCard";
import Crossing from "@/components/Crossing";

const MAILTO_BEARING = "mailto:hello@traeva.ie?subject=Bearing%20request&body=Brand%20website%3A%20";
const MAILTO_LAUNCH = "mailto:hello@traeva.ie?subject=EU%20Launch";
const MAILTO_IE = "mailto:hello@traeva.ie?subject=Amazon.ie%20Launch";
const MAILTO_GROWTH = "mailto:hello@traeva.ie?subject=Growth%20Partnership";

const fares = [
  {
    testid: "fare-bearing",
    dark: true,
    route: "DUB → ?",
    routeSub: "ASSESSMENT · VERDICT FIRST",
    klass: "Intelligence",
    name: "The Bearing",
    forText: '"Should we be on Amazon EU at all?"',
    desc: (
      <>
        A professional assessment of your brand ending in one stamp — GO, WAIT, or CONDITIONAL
        GO — with the evidence behind it: readiness check, market sequence, financial model,
        risk register. The full version is{" "}
        <strong>operator-prepared, signed, and yours to keep</strong> whoever you launch with.
        Founder call +€200.
      </>
    ),
    price: "Free",
    priceSub: "Operator version €690",
    cta: "Request →",
    href: MAILTO_BEARING,
  },
  {
    testid: "fare-eu-launch",
    route: "DUB → BER · PAR · AMS",
    routeSub: "EXECUTION · ZERO TO LIVE",
    klass: "Launch",
    name: "EU Launch",
    forText: '"Take us from zero to selling."',
    desc: (
      <>
        Everything between today and your first EU sale: account, Brand Registry, compliance,
        German and French listings built to convert, FBA logistics, launch advertising, 90 days
        of post-launch support. <strong>Live in 10–12 weeks.</strong> Up to 8 products; +€300
        each after.
      </>
    ),
    price: "€12,000",
    priceSub: "50% signing · 50% wk 12",
    cta: "Start →",
    href: MAILTO_LAUNCH,
  },
  {
    testid: "fare-amazon-ie",
    route: "DUB → DUB",
    routeSub: "DOMESTIC · THE PROVING GROUND",
    klass: "Home first",
    name: "Amazon.ie Launch",
    forText: '"Prove it at home before Europe."',
    desc: (
      <>
        Ireland’s own marketplace opened in 2025 — and most Irish brands still aren’t on it.
        Live in <strong>4–6 weeks</strong> with up to 5 products: reviews, real data, and an
        account that already works before the bigger move. Honest note: this won’t transform
        revenue. That’s Europe’s job. This removes the risk.
      </>
    ),
    price: "€3,000",
    priceSub: "50% signing · 50% live",
    cta: "Start →",
    href: MAILTO_IE,
  },
  {
    testid: "fare-growth",
    route: "ONGOING ∞",
    routeSub: "PARTNERSHIP · CHANNEL RUN FOR YOU",
    klass: "Growth",
    name: "Growth Partnership",
    forText: '"Run the channel properly."',
    desc: (
      <>
        Advertising, inventory, listings, account health, monthly reporting — founder-direct,
        always. The success fee is{" "}
        <strong>10% of EU revenue, capped at €10,000/month in writing</strong>, so our upside
        never punishes yours. We take over accounts we didn’t launch. New markets as fixed
        €1,500 projects.
      </>
    ),
    price: "€1,800",
    priceSub: "/month + capped 10% · 90-day exit",
    cta: "Talk →",
    href: MAILTO_GROWTH,
  },
];

export default function HowItWorks() {
  return (
    <main data-testid="how-it-works-page" className="pt-[88px] md:pt-[72px]">
      {/* ───── HERO ───── */}
      <section className="border-b border-line bg-paper">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-8 sm:pt-12 lg:pt-16 pb-12 sm:pb-16">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-5">
              <span className="w-9 h-px bg-coral" />
              Chapter 03 — Fares
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif-i italic text-[44px] sm:text-6xl lg:text-[80px] text-ink leading-[.98] tracking-[-.035em] max-w-5xl mb-7">
              Every price we charge,
              <br />
              printed on the <em className="text-navy-mid">ticket.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-[16px] font-light leading-[1.78] max-w-[600px] text-ink-muted">
              Four ways to travel. Independent — none requires another first. No discovery-call
              pricing, no quotes that drift.{" "}
              <strong className="text-ink font-medium">What’s on the ticket is what it costs.</strong>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───── FARES ───── */}
      <section className="bg-surface border-b border-line" id="fares" data-testid="fares-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-14 sm:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7">
            {fares.map((f, i) => (
              <Reveal key={f.testid} delay={i * 0.08}>
                <FareCard {...f} />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-10 text-[13px] font-light italic text-ink-muted max-w-2xl leading-[1.75]" data-testid="fares-note">
              These are independent doors, not steps. Plenty of brands launch without a Bearing.
              Some take the Bearing and launch with someone else —{" "}
              <b className="text-ink font-medium not-italic">the document is still theirs. That’s the point.</b>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───── CROSSING (timeline) ───── */}
      <Crossing />

      {/* ───── CLOSING CTA ───── */}
      <section data-testid="how-it-works-cta-section">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
          <Reveal>
            <h2 className="font-serif-i italic text-[44px] sm:text-5xl lg:text-[64px] leading-[.98] tracking-[-.035em] max-w-4xl text-ink">
              Start with the verdict.
              <br />
              <em className="text-coral">It costs nothing.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-xl text-ink-muted text-lg leading-[1.78]">
              <Brand /> issues a free Bearing for every brand that asks. One document. One
              decision. Whoever you launch with.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <a
                href={MAILTO_BEARING}
                data-testid="how-it-works-cta"
                className="inline-flex items-center gap-2 bg-coral text-white text-[14px] font-medium px-8 py-4 transition-transform hover:-translate-y-0.5 hover:bg-coral-dark"
              >
                Request your free Bearing <ArrowRight size={16} strokeWidth={1.6} />
              </a>
              <Link
                to="/bearing"
                className="inline-flex items-center gap-2 text-[14px] font-medium px-8 py-4 border border-ink text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                See a sample Bearing
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
